import { create } from "zustand";
import {
  FeatureFlagKey,
  FeatureFlagsResponseSchema,
  FeatureFlagValue,
  FeatureFlagValues,
  getAllFeatureFlagDefaults,
} from "@forgepoint/shared";
import { fetchApi } from "@/lib/api";
import { useAuthStore } from "./authStore";

interface FeatureFlagState {
  flags: FeatureFlagValues;
  loaded: boolean;
  loading: boolean;
  error: string | null;
}

interface FeatureFlagActions {
  fetchFlags: () => Promise<void>;
  /**
   * Reads a flag value from the store. Falls back to the registry default
   * if flags have not been fetched yet (e.g. before the API call returns).
   * @param key - The flag key from the central registry.
   * @returns The typed flag value.
   */
  getFlag: <K extends FeatureFlagKey>(key: K) => FeatureFlagValue<K>;
  reset: () => void;
}

const initialState: FeatureFlagState = {
  flags: getAllFeatureFlagDefaults(),
  loaded: false,
  loading: false,
  error: null,
};

/**
 * Zustand store for feature flag values evaluated by the backend. Initial
 * values are the code-defined defaults so callers can read flags before
 * the network request completes.
 */
export const useFeatureFlagStore = create<
  FeatureFlagState & FeatureFlagActions
>()((set, get) => ({
  ...initialState,

  fetchFlags: async () => {
    const user = useAuthStore.getState().user;
    if (!user) {
      return;
    }
    const requestUserId = user.id;
    const requestOrgId = user.organization?.id;

    set({ loading: true, error: null });

    try {
      const response = await fetchApi("feature-flags", "GET");
      const currentUser = useAuthStore.getState().user;
      if (
        !currentUser ||
        currentUser.id !== requestUserId ||
        currentUser.organization?.id !== requestOrgId
      ) {
        return;
      }
      if (!response.ok) {
        throw new Error("Failed to fetch feature flags");
      }
      const data = FeatureFlagsResponseSchema.parse(response.data);
      set({
        flags: { ...getAllFeatureFlagDefaults(), ...data.flags },
        loaded: true,
        loading: false,
      });
    } catch (err) {
      const currentUser = useAuthStore.getState().user;
      if (
        !currentUser ||
        currentUser.id !== requestUserId ||
        currentUser.organization?.id !== requestOrgId
      ) {
        return;
      }
      set({
        error: err instanceof Error ? err.message : "Unknown error occurred",
        loading: false,
      });
    }
  },

  getFlag: <K extends FeatureFlagKey>(key: K): FeatureFlagValue<K> => {
    return get().flags[key];
  },

  reset: () => set(initialState),
}));

/**
 * React hook for reading a single typed feature flag value. Subscribes the
 * component so it re-renders when the flag value changes.
 * @param key - The flag key from the central registry.
 * @returns The typed flag value.
 */
export function useFeatureFlag<K extends FeatureFlagKey>(
  key: K,
): FeatureFlagValue<K> {
  return useFeatureFlagStore((state) => state.flags[key]);
}
